/**
 * The permission catalogue — every capability the system can grant, declared once.
 *
 * WHY ONE FLAT LIST OF STRINGS rather than a nested resource/action structure: this list is
 * consumed by four things that must agree exactly — the API middleware, the web route guards,
 * the mobile navigation, and the AI tool registry. A flat `resource.subresource:action`
 * string is greppable, storable in a database column, and serialisable into a JWT. A nested object
 * is none of those.
 *
 * The AI tool registry is the reason this matters more here than in a typical app: each agent tool
 * declares the permission it requires, and the tool list handed to the model is filtered by
 * `can()` per request. A permission that does not exist here cannot be required by a tool, and a
 * tool whose permission the caller lacks is invisible to the model. That filter — not the prompt —
 * is the security boundary.
 */

export const PERMISSIONS = [
  // ---- Organisation & platform -------------------------------------------------
  'org:read',
  'org:update',
  'org.settings:read',
  'org.settings:update',
  'org.branding:update',
  'org.billing:read',
  'org.billing:update',
  // Company groups (ADR 0034): create another company of the group from inside, and manage who has
  // access to which companies of it. The second also needs the caller to be a group owner.
  'org.company:create',
  'org.group:manage',
  // Legal Agreement / E-Signature module (see docs/adr/0015-agreement-esignature-architecture.md):
  // the tenant's own signed-order-form agreement (MSA+SOF+DPA), not the platform-owned templates —
  // there is no tenant-facing permission for template maintenance at all in this phase.
  'agreement:read',
  'agreement:manage',
  'legal_entity:read',
  'legal_entity:create',
  'legal_entity:update',
  'location:read',
  'location:manage',
  'department:read',
  'department:manage',
  'post:read',
  'post:manage',

  // ---- Identity & access -------------------------------------------------------
  'user:read',
  'user:create',
  'user:update',
  'user:deactivate',
  // Clear someone's salary PIN so they set a new one (ADR 0041). Owner-only by default.
  'salary_pin:reset',
  'user.impersonate:execute',
  'role:read',
  'role:assign',
  'sso:manage',
  'apikey:manage',
  'audit:read',
  'audit:export',

  // ---- People ------------------------------------------------------------------
  'employee:read',
  'employee:create',
  'employee:update',
  'employee:delete',
  'employee:export',
  'employee.pii:read',
  /** Download full Aadhaar, PAN and bank account numbers (decrypted) in the employee export. */
  'employee.pii:export',
  /** Find colleagues in the Employee Directory — work-safe fields only (directory.contract.ts). */
  'employee.directory:read',
  'employee.document:read',
  'employee.document:manage',
  'employment:read',
  'employment:create',
  'employment:update',
  'employment.transfer:execute',
  'employment.exit:execute',
  /**
   * See OTHER people's pay, within the grant's scope. Split from `compensation.own:read` (owner
   * decision D1, ADR 0023): off by default for `manager` and `hr_manager`; an organisation turns it
   * on for them on the Roles & Permissions page.
   */
  'compensation:read',
  /** See ONE'S OWN pay. Every employee holds it; can.ts refuses any `.own` permission on anyone else. */
  'compensation.own:read',
  'compensation:update',
  'compensation.cycle:manage',
  'org_chart:read',
  'org_chart:update',

  // ---- Attendance & time -------------------------------------------------------
  /** One's OWN attendance. Every role spreads EMPLOYEE, so this alone never reaches anyone else (P1-19). */
  'attendance:read',
  /** Other people's attendance, at the grant's scope: a team, a department, the org. Split from
   * `attendance:read` the way `compensation:read` is split from `compensation.own:read` (P1-19). */
  'attendance.team:read',
  /** The company's attendance rules, such as regularisation limits (P1-14). */
  'attendance.policy:manage',
  'attendance:punch',
  'attendance:update',
  'attendance:approve',
  'attendance.regularisation:request',
  'attendance.regularisation:approve',
  'attendance.cycle:freeze',
  'shift:read',
  'shift:manage',
  'shift.roster:manage',
  /** CRUD on ShiftGroup/RotationAssignment (Shift Groups, Rotating Shift/Work Type Assignments) and
   * the manual "regenerate now" trigger. Scoped the same way as `shift.roster:manage` — a manager
   * holding it reaches only their own reporting tree, via the same `resolveRosterPersonScope` every
   * roster-management route already uses. */
  'shift.rotation:manage',
  'shift.change:request',
  'shift.change:approve',
  'attendance.od:request',
  'attendance.od:approve',
  /** CRUD on EarlyExitPenaltyPolicy — configuring grace/warning/penalty overrides and payroll caps.
   * Deliberately separate from `shift:manage`: editing policy scope (person/grade/role/legal-entity)
   * is an HR/payroll-policy action, not a shift-catalog one. */
  'attendance.penalty:manage',
  'attendance.early_exit_waiver:request',
  'attendance.early_exit_waiver:approve',
  'timesheet:read',
  'timesheet:submit',
  'timesheet:approve',
  'overtime:approve',

  // ---- Projects & delivery -------------------------------------------------------
  'project:read',
  'project:create',
  'project:update',
  'project:archive',
  'project.member:manage',
  'project.task:read',
  'project.task:manage',
  'project.milestone:manage',
  'project.analytics:read',
  'work_plan:read',
  'work_plan:submit',
  // Manager decides the tasks an employee proposed at check-in (ADR 0009).
  'work_plan:approve',
  // HR designs the check-in / check-out forms: mode and fields.
  'work_form:manage',
  // HR configures which conditions block clock-out (ADR 0019): the EOD report, overdue tasks,
  // mandatory learning. Reading readiness/being gated by it needs no permission of its own — every
  // signed-in employee is subject to their own org's policy.
  'clock_out_policy:manage',
  'biometric_device:manage',

  // ---- Leave -------------------------------------------------------------------
  'leave:read',
  'leave:request',
  'leave:approve',
  'leave:cancel',
  'leave.balance:read',
  'leave.balance:adjust',
  'leave.policy:read',
  'leave.policy:manage',
  // Employee asks to be paid for leave days (ADR 0031). Self scope confines it, like the loan request.
  'leave.encashment:request',
  // Employee asks to be credited comp-off for a holiday or weekly off they worked (Phase 4b). Self
  // scope confines it; the credit exists only once the approval lands.
  'leave.comp_off:request',
  // Cancel someone else's leave for them: a manager in their reporting tree, HR across the org
  // (Phase 4b). The employee cancelling their own leave needs only `leave:cancel`.
  'leave.cancel:on_behalf',
  // HR's year-end runs: read drafts and results, and execute (apply or discard) them.
  'leave.year_end:read',
  'leave.year_end:execute',
  // HR's leave reports: register, lapses, trends, loss of pay (Phase 4d). Reach is the grant's scope.
  'leave.report:read',
  // Leave plans (ADR 0032): named rule bundles aimed at departments, locations, grades or
  // employment types. A plan reaches many people at once, so it is not `leave.policy:manage`.
  'leave.plan:read',
  'leave.plan:manage',
  'holiday_calendar:manage',

  // ---- Payroll -----------------------------------------------------------------
  'payroll:read',
  'payroll.run:create',
  'payroll.run:execute',
  'payroll.run:approve',
  /**
   * Lock an APPROVED run as the final payroll result (ADR 0014, D2). The calculation version, its results
   * and its rules snapshot cannot change afterwards. A separate act from approving: it is held by the same
   * roles as `payroll.run:approve` and by no payroll-operations role, and the person who submitted the run
   * can never use it on that run.
   */
  'payroll.run:finalize',
  /**
   * Release a FINALIZED run for execution and employee access (ADR 0014, D3). It publishes no payslip and
   * moves no money; it is the gate publishing and paying each require. Same holders as `payroll.run:finalize`.
   */
  'payroll.run:release',
  'payroll.run:reopen',
  'payroll.input:manage',
  'payroll.rules:read',
  'payroll.rules:manage',
  /**
   * Validate, publish and archive a rule version or an entity's rule configuration. Separate from
   * `payroll.rules:manage` (which drafts) so the person who prepares a change is not the one who
   * puts it live — and the API additionally refuses to let anyone publish a draft they authored.
   */
  'payroll.rules:publish',
  /**
   * Switch a legal entity's payroll onto the rule engine (`engine` mode, ADR 0012). Separate from
   * `payroll.rules:manage` — which may turn shadow measurement on and off — because this is the act that
   * changes what people are PAID. Held by the organisation owner only; `manage` alone can never do it.
   */
  'payroll.rules:rollout',
  /**
   * Create and end scoped overrides of a rule parameter — for a legal entity or for one employment.
   * An override changes what somebody is paid, so it is not implied by `payroll.rules:manage`, and
   * the API refuses an override of one's own employment.
   */
  'payroll.rules.override:manage',
  /** Create pay groups and set their default cut-off / pay-date rules. Owner-level: it moves every cycle. */
  'payroll.paygroup:manage',
  /** Maintain individual cycle dates inside rules someone else defined. */
  'payroll.calendar:maintain',
  /** Propose a salary component or a change to one — it goes live only after approval. */
  'payroll.component:propose',
  /** Approve a proposed salary component change — the second half of the dual review. */
  'payroll.component:approve',
  /**
   * Sign off one team's payroll inputs for a cycle. A manager's half of input preparation: it
   * attests to the inputs they own, and it is NOT an approval of the payroll itself.
   */
  'payroll.run.signoff:team',

  // ---- Income-tax declarations & proofs ----------------------------------------
  //
  // DELIBERATELY FOUR PERMISSIONS, NOT ONE. A tax declaration carries an employee's rent, their
  // insurance, their landlord's PAN and their home-loan statements. Approving a payroll run needs
  // none of that, so approving a payroll run must not imply reading it: `payroll.run:approve` and
  // `payroll.tax.proof:verify` are unrelated grants, and no role holds the second merely because
  // it holds the first.
  /** See the verification queue and the regime summary — aggregate and queue reads, in scope. */
  'payroll.tax:read',
  /** Elect a regime, maintain declarations, and upload proofs FOR ONESELF. Every employee holds it. */
  'payroll.tax.own:manage',
  /** Verify another employee's proofs. Never satisfiable against oneself — see can.ts. */
  'payroll.tax.proof:verify',
  /** Publish a tax-year configuration: categories, calculation parameters, submission window. */
  'payroll.tax.config:manage',

  // ---- FBP (Flexible Benefits Plan) declarations --------------------------------
  //
  // A SEPARATE permission from `payroll.tax.own:manage`, deliberately: FBP allocation is a
  // compensation-structuring choice capped by the employee's flexi pool amount, not an income-tax
  // evidence claim — the two are different business processes (FbpDeclaration's own header
  // comment), so holding one must not imply the other.
  /** Declare, revise, and submit one's own FBP allocation for a fiscal year. Every employee holds it. */
  'payroll.fbp.own:manage',
  'payslip:read',
  'payslip.own:read',
  'payslip:publish',
  'bank_file:generate',
  'loan:manage',
  /** Apply for a loan/advance for yourself. Distinct from `loan:manage` (HR/Admin creating one
   * directly, or viewing every record) — see roles.ts's EMPLOYEE vs PAYROLL_ADMIN blocks. */
  'payroll.loan:request',
  /** A manager's recommendation on a report's loan/advance request — the same shape as
   * `expense:approve`/`leave:approve`, scoped to `reporting_tree` and never a direct disbursement. */
  'payroll.loan:recommend',
  /** Request an EWA withdrawal for yourself. Eligibility and caps are always recalculated
   * server-side (never trusted from the client), so this permission only gates who may ask. */
  'payroll.ewa:request',
  'fnf:execute',

  // ---- Payments, holds and off-cycle (ADR 0015) ---------------------------------
  //
  // PREPARE, AUTHORISE AND TRANSMIT ARE THREE PERMISSIONS, because they are three people's acts. The
  // payroll desk assembles a batch and sends it; the owner (the "Super Admin") releases the money in
  // between; and the API additionally refuses the person who prepared a batch version to authorise it,
  // because the owner inherits the payroll desk's grants too.
  /** See payment batches, obligations and attempts: amounts and MASKED destinations. */
  'payroll.payment:read',
  /** Assemble a batch from payable results, submit it for authorisation, cancel it before transmission. */
  'payroll.payment:prepare',
  /** Authorise the exact contents of a batch. Held by the owner only. Never by its preparer. */
  'payroll.payment:authorise',
  /** Export an AUTHORISED batch's bank file, transmit it to a provider, retry a definitive failure. */
  'payroll.payment:transmit',
  /**
   * Record a payment outcome the system did not observe, with evidence (a bank reference and value date),
   * and resolve an unknown outcome. Finance and the owner. Never against one's own payment.
   */
  'payroll.payment:reconcile',
  /** A manager's view of whether their reports were paid — status only, never amounts or bank details. */
  'payroll.payment.team:read',
  /** One's own payment status. Every employee holds it. */
  'payroll.payment.own:read',
  /** Ask for a team member's salary to be held, with a reason. A request, not a hold. */
  'payroll.hold:request',
  /** Prepare a hold request for approval, and withdraw one before it takes effect. The payroll desk. */
  'payroll.hold:manage',
  /** Put a prepared hold into effect, and release an active one. The owner. Never on one's own salary. */
  'payroll.hold:approve',
  /** Create and calculate an off-cycle payment inside the payroll ledger, and submit it for approval. */
  'payroll.offcycle:manage',
  /** Approve an off-cycle payment. The owner. Never one's own. */
  'payroll.offcycle:approve',
  /** Set whether payslips publish regardless of payment, after it, or only by hand. The owner. */
  'payslip.policy:manage',

  // ---- Full & Final settlement (ADR 0015) --------------------------------------
  /** Read settlements, their calculation versions and clearances. Narrower than `payroll:read`. */
  'fnf:read',
  /** Approve the exact calculation version of a settlement. The owner. Never one's own settlement. */
  'fnf:approve',
  /** Define which departments clear an exit, who owns each clearance and what it gates. HR. */
  'fnf.clearance:manage',

  // ---- Statutory filings, challans & Form 16 -----------------------------------
  //
  // FOUR PERMISSIONS FOR WHAT LOOKS LIKE ONE SCREEN, because three genuinely different things
  // happen on it. Building an ECR file out of a finished payroll is clerical work. Signing it is a
  // named officer attesting to a statutory return in their own name. Releasing the challan is money
  // leaving a bank account. One permission covering all three would mean a payroll executive could
  // prepare, authorise and pay a ₹42 lakh remittance without anyone else touching it — which is the
  // exact control a maker-checker separation exists to impose.
  /** Generate statutory return files and record portal acknowledgements. The maker. */
  'statutory.filing:manage',
  /** Sign and authorise a generated return. Never satisfiable by its own generator — see can.ts. */
  'statutory.filing:approve',
  /** Release the challan for payment once the return is authorised. Finance's half. */
  'statutory.remittance:approve',
  /** See one's OWN PF/ESI/PT/TDS contributions and Form 16. Every employee holds it. */
  'statutory.own:read',
  /** Produce Form 16 Part B and attach a TRACES-issued Part A. */
  'form16:generate',
  /** Release generated Form 16 certificates to the employees they belong to. */
  'form16:issue',

  // ---- Payroll accounting & reporting ------------------------------------------
  //
  // `report:read`/`report:export` are the generic reporting grants and stay that way. These are
  // about SALARY figures specifically: a person who may read the org's engagement survey report has
  // not thereby been given the payroll register.
  /** Prepare, export and post the payroll journal to the general ledger. */
  'payroll.gl:manage',
  /** Close a payroll accounting period, after which nothing further books into it. */
  'payroll.period:close',
  /**
   * Read the aggregated payroll cost of one's OWN reporting line — team totals and overtime, never
   * individual packages, statutory balances, or anything outside the tree. Held at
   * `reporting_tree` scope; the API returns a narrowed response rather than a fuller one filtered
   * client-side.
   */
  'payroll.report.team:read',

  // ---- Salary queries ----------------------------------------------------------
  /** Raise a query about one's own pay and follow its thread. Every employee holds it. */
  'payroll.query.own:manage',
  /** Read queries raised by others, within scope. A manager's grant is reporting_tree. */
  'payroll.query:read',
  /** Answer a query officially, schedule an adjustment, and close it. The payroll desk. */
  'payroll.query:resolve',

  // ---- Expenses & travel -------------------------------------------------------
  'expense:read',
  'expense:submit',
  /** ADDED 2026-09-28 (Keka parity) — create a claim naming someone ELSE as the claimant (a manager
   * for their own report, HR for anyone in the org), rather than only being able to file for
   * oneself. Same shape as `expense.advance:issue` (2026-09-24), and deliberately following that
   * more recent, same-module precedent over `leave:request`'s older "reuse the permission at a
   * wider scope" approach — a distinct permission keeps "may file for someone else" separate from
   * `expense:submit`'s own edit/delete/withdraw reach, which `updateClaim`'s own comment already
   * flags as a gap were it ever widened. The claim still goes through the same approval workflow
   * afterwards — this only changes who may type it in. */
  'expense.claim:file_for',
  'expense:approve',
  'expense:settle',
  'expense.policy:manage',
  /** See the category catalog — including deactivated categories — without being able to change
   * it. What the admin screen's visibility gates on, so a read-only auditor can find it, matching
   * every other admin section's `:read` gate (`nav.ts`); every mutation stays on `:manage`. */
  'expense.policy:read',
  /** Request a cash advance for oneself, ahead of a trip or spend — TG-5. Approving one goes
   * through the same reporting-manager step as a claim (`expense:approve` gates that queue's
   * visibility; the decision itself is workflow-eligibility gated, like `approveClaim`). */
  'expense.advance:request',
  /** Disburse an approved advance, write one off, and see every advance org-wide — the finance
   * desk's half, same population as `expense:settle`. Never held by whoever approved the request. */
  'expense.advance:manage',
  /** ADDED 2026-09-24 — create an advance request FOR someone else (a manager for their own
   * report, HR for anyone in the org), rather than only being able to approve one an employee
   * filed themselves. The request still goes through the same approval workflow afterwards —
   * this only changes who may type it in, not who decides it. */
  'expense.advance:issue',
  // Travel requests (R-P10): `travel:request` is one's own trips. `travel:approve` is NOT held
  // by whoever decides the workflow step (that's assignment-gated, like `expense:approve`'s own
  // decide routes) — it gates viewing someone else's requests and the "travel desk" booking-status
  // action (`travelRequestContract.updateBooking`).
  'travel:request',
  'travel:approve',
  // Travel Desk (ADR 0030), extending travel requests above. Every trip in scope: file on
  // someone's behalf, who-is-travelling, the travel reports.
  'travel:read',
  // The desk. Which queues an agent reads and books is their desk teams' coverage, decided by the
  // API from membership, not by the grant's scope (ADR 0030, like helpdesk teams in ADR 0028).
  'travel.desk:read',
  'travel.desk:book',
  // Desk teams, members, coverage, vendors, reassignment, every queue and the desk settings.
  'travel.desk:manage',
  // Travel policies, entitlements, city tiers, per-diem rules and the module switch (D33).
  'travel.policy:manage',

  // ---- Chat --------------------------------------------------------------------
  'chat:read',
  'chat:send',
  'chat.channel:create',
  'chat.channel:manage',
  'chat.message:delete_any',
  'chat.announcement:broadcast',
  'chat.export:execute',
  'chat.import:execute',
  'chat.legal_hold:manage',
  'chat.dlp:review',
  'chat.settings:manage',

  // ---- Announcements (Engage, ADR 0029) ----------------------------------------
  'announcement:read',
  'announcement:manage',

  // ---- Workflow ----------------------------------------------------------------
  'workflow:read',
  'workflow:manage',
  'workflow.instance:approve',
  'workflow.delegate:manage',

  // ---- Recruitment -------------------------------------------------------------
  'requisition:read',
  'requisition:create',
  'requisition:update',
  'requisition:close',
  'requisition:approve',
  'candidate:read',
  'candidate:manage',
  'interview:read',
  'interview:schedule',
  'interview.feedback:submit',
  'offer:create',
  'offer:send',
  'offer:approve',
  'offer:rescind',
  'offer.compensation:read',
  'recruitment.analytics:read',
  'referral:read',
  'referral:create',

  // ---- Onboarding / offboarding ------------------------------------------------
  'onboarding:read',
  'onboarding:manage',
  'offboarding:read',
  'offboarding:manage',
  'offboarding.team:read',
  'offboarding.task:complete',
  'offboarding.policy:manage',
  'exit_interview:conduct',
  'asset:manage',

  // ---- Asset management ---------------------------------------------------------
  //
  // Deliberately separate from `asset:manage` above, which onboarding uses to check off "laptop
  // issued" during a new-hire checklist and stays exactly as it was. These nine cover the asset
  // register itself: the inventory, requests for equipment, and who may see what an asset cost.
  /** See the asset register — inventory, assignments and status. */
  'asset:read',
  /** Add a new asset to the register. */
  'asset:create',
  /** Edit an asset's record — condition, location, specification. */
  'asset:edit',
  /** Assign an asset to a person. */
  'asset:assign',
  /** Transfer an assigned asset from one person or location to another. */
  'asset:transfer',
  /** Request an asset for oneself. Every employee holds it. */
  'asset.request:create',
  /** Approve or reject an asset request. */
  'asset.request:approve',
  /** Maintain the asset category taxonomy (categories, depreciation class, etc.). */
  'asset.category:manage',
  /** See what an asset cost and its depreciated value — kept separate from `asset:read` the same
   * way `employee.pii:read` is kept separate from `employee:read`. */
  'asset.financial:read',

  /** Delete a draft or unallocated asset from the register. */
  'asset:delete',
  /** Check in and return an assigned asset. */
  'asset:return',
  /** Retire an asset from service (due to age, damage, or obsolescence). */
  'asset:retire',
  /** Dispose of a retired asset (scrap, sale, recycling, donation). */
  'asset:dispose',

  // ---- Asset Maintenance & Issues -----------------------------------------------
  /** View asset maintenance requests, diagnostics, and work orders. */
  'asset:maintenance:read',
  /** Report a new maintenance issue or hardware fault. */
  'asset:maintenance:create',
  /** Update maintenance diagnostics, work orders, technician assignment, or resolution. */
  'asset:maintenance:manage',
  /** Enter vendor invoice/repair costs on maintenance work orders without broad asset financial read. */
  'asset.maintenance:cost_input',

  // ---- Asset Audit & Physical Reconciliation -----------------------------------
  /** View physical asset audits, inventory schedules, and discrepancy reports. */
  'asset:audit:read',
  /** Schedule and create new physical asset audit events. */
  'asset:audit:create',
  /** Reconcile, verify, and resolve physical audit scans against register records. */
  'asset:audit:verify',
  /** Edit an audit run that is in progress — name, due date, auditors — or cancel it. */
  'asset:audit:manage',
  /** Close an audit run with its summary and produce the discrepancy report. */
  'asset:audit:close',

  // ---- Asset Analytics & Financial Governance -----------------------------------
  /** View asset lifecycle analytics, depreciation summaries, and utilization metrics. */
  'asset:analytics:read',
  /** Export asset lifecycle and valuation analytics data. */
  'asset:analytics:export',
  /** Manage asset valuation, depreciation rules, and capital asset accounting parameters. */
  'asset:financial:manage',

  // ---- Asset Configuration & Vendors (Administration) ---------------------------
  //
  // A second layer over the asset register above: the Configuration/Vendors admin screens.
  // `asset.category:manage` (above) already covers editing the category taxonomy itself — these
  // add a read-only counterpart plus the type/vendor/export axes that taxonomy editing alone
  // doesn't cover. `asset.category:manage` keeps ONE meaning across both areas: see roles.ts for
  // exactly who holds it (hr_manager, hr_admin, it_admin — not auditor).
  /** Read the asset category taxonomy without holding `asset.category:manage`. */
  'asset.category:read',
  /** Read the asset type catalog. */
  'asset.type:read',
  /** Manage the asset type catalog (create/edit types within a category). */
  'asset.type:manage',
  /** Read the vendor directory. */
  'asset.vendor:read',
  /** Manage the vendor directory. */
  'asset.vendor:manage',
  /** Export Asset Configuration or Vendor data as CSV. */
  'asset:export',
  /** Read asset procurement batches (Administration > Asset Batches). */
  'asset.batch:read',
  /** Create/edit asset batches and bulk-import assets into one. */
  'asset.batch:manage',

  // ---- Performance & learning --------------------------------------------------
  'goal:read',
  'goal:manage',
  'goal:create',
  'goal:edit:self',
  'goal:edit:team',
  'goal:approve',
  'goal:delete:self',
  'goal.checkin:create',
  'review:read',
  'review:submit',
  'review:self:submit',
  'review.cycle:manage',
  'review.calibrate:execute',
  // Performance Reviews (Section 4) net-new — see the plan's "Permissions" section. `review.manager
  // :submit` is the gaming-vector permission: employees hold `review:submit`, so a manager's rating
  // write must require a DIFFERENT permission or an employee could write `managerRating` on their
  // own review. `review:manage` is scoped narrower than `review.cycle:manage` — reassign/reopen one
  // review without the permission that can close the whole cycle and gate everyone's pay.
  'review.manager:submit',
  'review:manage',
  // Review appeals (PRF-10) — deliberately its own four keys, not folded into `review:manage`:
  // an appeal queue is a narrower, HR-specific surface than "reassign/reopen any review", and
  // `:approve`/`:reject` are split (not one `:decide`) for the same reason `merit:approve`/
  // `merit:reject` are — a role can hold one without the other. `:manage` is reopening a resolved
  // appeal, a strictly rarer/more sensitive action than deciding one the first time.
  'review:appeal:read',
  'review:appeal:approve',
  'review:appeal:reject',
  'review:appeal:manage',
  'competency:manage',
  // Skills library (PRF-16) — dedicated keys, never competency:manage (that stays scoped to
  // Competencies). `skill:read` covers viewing the catalogue and any visible person's ratings
  // (self-scope: submitting your own self-rating, same "read implies acting on your own record"
  // shape `goal:read`'s self scope already has). `skill:manage` covers authoring the catalogue and
  // a manager confirming a report's rating.
  'skill:read',
  'skill:manage',
  // Merit & compensation planning (PRF-9/PRF-29) — deliberately separate from `review.manager
  // :submit`/`review.cycle:manage`. A manager who can write a peer's review rating must not
  // thereby be able to move money: the increment matrix, recommendation, and worksheow-submit
  // actions below are the ONLY permissions that reach a MeritRecommendation/MeritMatrix row.
  // `merit:read` — view the matrix, a worksheet, or budget vs cost.
  'merit:read',
  // `merit:submit` — a manager edits "their %"/promotion flag and submits their own reporting-
  // tree worksheet. Never grants matrix authoring.
  'merit:submit',
  // `merit:approve` / `merit:reject` — decide the cycle's merit_cycle_budget workflow instance
  // (see workflow-entity-types.ts). Kept as two keys, not one, matching review:appeal's split and
  // letting a role approve without also being handed reject (or vice versa).
  'merit:approve',
  'merit:reject',
  // `merit:manage` — author the increment matrix (bands x pay-band position -> %), total budget,
  // effective date.
  'merit:manage',
  'improvement_plan:read',
  'improvement_plan:manage',
  'feedback:give',
  'feedback:read',
  'feedback.request:create',
  'feedback.identity:read',
  'one_on_one:read',
  'one_on_one:schedule',
  'learning:read',
  'learning:manage',
  'learning.assign:execute',
  // Release a clock-out block or waive a webcam requirement on a trainee's task, and revoke an
  // issued certificate. HR admin only, and self-excluded (SELF_EXCLUDED_PERMISSIONS in can.ts) —
  // nobody releases or waives their own gate.
  'learning.override:execute',
  // View webcam footage captured during a training task. HR admin only, self-excluded, and every
  // view is access-logged (plan D10). Deliberately not granted to managers, who see only a
  // verification status.
  'learning.recording:view',
  // Run training across the companies of a group from one place: the all-companies course library,
  // assign/copy to several companies, and the live-session calendar (ADR 0036). Also needs the caller
  // to be a group owner, and `learning:manage` company-wide in each company the call touches.
  'learning.group:manage',

  // ---- Compliance & documents --------------------------------------------------
  'policy:read',
  'policy:manage',
  'policy:acknowledge',
  'compliance:read',
  'compliance:manage',
  'posh.case:read',
  'posh.case:manage',
  'grievance:raise',
  'grievance:manage',
  'dsr:manage',

  // ---- Helpdesk & engagement ---------------------------------------------------
  'ticket:read',
  'ticket:create',
  // A helpdesk agent: work tickets in the categories of the helpdesk teams one belongs to. Which
  // tickets is decided by team membership, not by the grant's scope (ADR 0028).
  'ticket:manage',
  // The Helpdesk Manager's three (ADR 0028): every ticket in every category, confidential ones
  // included; categories, teams, SLA targets and hours; the reports.
  'ticket.all:manage',
  'ticket.config:manage',
  'ticket.report:read',
  'survey:read',
  'survey:respond',
  'survey:manage',
  'recognition:give',
  'recognition:manage',

  // ---- Analytics ---------------------------------------------------------------
  'report:read',
  'report:create',
  'report:export',
  'analytics.compensation:read',
  'analytics.attrition:read',
  // Performance Insights (Section 5 / plan Phase 3) — the filtered org analytics view at
  // /performance/insights. Distinct from `review:read`/`report:read`: this gates cohort-level
  // aggregates (rating distribution, team/department breakdowns, cycle completion) that a manager
  // or auditor should see across people they don't individually hold `review:read` over. `:export`
  // is held separately and narrower — see roles.ts, the auditor never holds it.
  'analytics.performance:read',
  'analytics.performance:export',

  // ---- AI ----------------------------------------------------------------------
  'ai.copilot:use',
  'ai.agent.write:execute',
  'ai.audit:read',
  'ai.settings:manage',

  // ---- Integrations ------------------------------------------------------------
  'integration:read',
  'integration:manage',
  'import:execute',
  'webhook:manage',
  // Connected applications (ADR 0033): employees created in outside applications. Separate from
  // `integration:*` above, which is the calendar/Slack connection. Who holds these is each company's
  // choice in Settings -> Roles; roles.ts only proposes a starting set.
  'app_integration:read',
  'app_integration:manage',
  'app_integration:sync',

  // ---- Notifications -------------------------------------------------------------
  // HR/admin decides everything here; employees configure nothing (settled product decision — see
  // the notification module plan). There is deliberately no employee-facing permission at all.
  'notification.config:read',
  'notification.config:manage',
  'notification.delivery:read',
] as const;

export type Permission = (typeof PERMISSIONS)[number];

const PERMISSION_SET: ReadonlySet<string> = new Set(PERMISSIONS);

export function isPermission(value: string): value is Permission {
  return PERMISSION_SET.has(value);
}
